/**
 * Crypto Service
 * Handles all cryptographic operations for ShareGuard's end-to-end encryption
 * This service is responsible for:
 * - Key generation and management
 * - Password-based key derivation
 */
class CryptoService {
  constructor() {
    // Store user's keys in memory (never persisted to disk)
    this.privateKey = null;
    this.publicKey = null;
    this.keyId = null;

    // Constants for file chunking and encryption
    this.CHUNK_SIZE = 95 * 1024 * 1024; // 95MB chunks for efficient processing
    this.AUTH_TAG_SIZE = 16; // AES-GCM authentication tag size
    this.IV_SIZE = 12; // Size of initialization vector in bytes
  }

  getIVSize() {
    return this.IV_SIZE;

  }

  /**
   * Converts ArrayBuffer to Base64 string
   * Used for storing binary data in JSON
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Converts Base64 string to ArrayBuffer
   * Used for converting stored data back to binary
   */
  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Generates RSA key pair for asymmetric encryption
   * Used during user registration or key rotation
   */
  async generateKeyPair() {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      true,
      ["encrypt", "decrypt"]
    );
    return keyPair;
  }

  /**
   * Exports public key to Base64 string
   * Used for sharing with other users
   */
  async exportPublicKey(publicKey) {
    const publicKeyBuffer = await window.crypto.subtle.exportKey(
      "spki",
      publicKey
    );
    return this.arrayBufferToBase64(publicKeyBuffer);
  }

  /**
   * Exports private key to Base64 string
   * Used for backup or key rotation
   */
  async exportPrivateKey(privateKey) {
    const privateKeyBuffer = await window.crypto.subtle.exportKey(
      "pkcs8",
      privateKey
    );
    return this.arrayBufferToBase64(privateKeyBuffer);
  }

  /**
   * Derives encryption key from user's password
   * Uses PBKDF2 for key stretching
   */
  async deriveKeyFromPassword(password, salt) {
    const passwordBuffer = new TextEncoder().encode(password);
    const passwordKey = await window.crypto.subtle.importKey(
      "raw",
      passwordBuffer,
      "PBKDF2",
      false,
      ["deriveKey"]
    );

    return window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt || window.crypto.getRandomValues(new Uint8Array(16)),
        iterations: 100000,
        hash: "SHA-256",
      },
      passwordKey,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  /**
   * Encrypts user's private key with password-derived key
   * Used for secure storage of private key
   */
  async encryptPrivateKey(privateKey, password) {
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    const aesKey = await this.deriveKeyFromPassword(password, salt);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    const privateKeyBuffer = await window.crypto.subtle.exportKey(
      "pkcs8",
      privateKey
    );

    const encryptedPrivateKeyBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      aesKey,
      privateKeyBuffer
    );
    return {
      iv: this.arrayBufferToBase64(iv),
      salt: this.arrayBufferToBase64(salt),
      data: this.arrayBufferToBase64(encryptedPrivateKeyBuffer)
    };
  }

  /**
   * Decrypts user's private key using password
   * Used during login or key access
   */
  async decryptPrivateKey(encryptedPrivateKey, password) {
    const { iv, salt, data } = encryptedPrivateKey;
    const aesKey = await this.deriveKeyFromPassword(password, this.base64ToArrayBuffer(salt));
    
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: this.base64ToArrayBuffer(iv) },
      aesKey,
      this.base64ToArrayBuffer(data)
    );
    return window.crypto.subtle.importKey(
      "pkcs8",
      decryptedBuffer,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      true,
      ["decrypt"]
    );
  }

  /**
   * Decrypts user's private key using a derived key
   * Used during session restore
   */
  async decryptPrivateKeyWithDerivedKey(encryptedPrivateKey, derivedKey) {
    const { iv, data } = encryptedPrivateKey;
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: this.base64ToArrayBuffer(iv) },
      derivedKey,
      this.base64ToArrayBuffer(data)
    );
    return window.crypto.subtle.importKey(
      "pkcs8",
      decryptedBuffer,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      true,
      ["decrypt"]
    );
  }

  /**
   * Generates AES-GCM key for file encryption
   * Each file gets its own unique key
   */
  async generateFileKey() {
    return window.crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  /**
   * Encrypts a small file or file chunk using AES-GCM
   * Used for individual chunks or small files that fit in memory
   */
  async encryptFileChunk(file, fileKey) {
    // Generate a unique IV for the file
    const iv = window.crypto.getRandomValues(new Uint8Array(this.IV_SIZE));
    const fileBuffer = await file.arrayBuffer();
    const encryptedFileBuffer = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, fileKey, fileBuffer);
    
    // Create a new ArrayBuffer that contains IV + encrypted data
    const combinedBuffer = new ArrayBuffer(iv.byteLength + encryptedFileBuffer.byteLength);

    // Copy IV to the beginning
    const combinedView = new Uint8Array(combinedBuffer);
    combinedView.set(iv, 0);
    combinedView.set(new Uint8Array(encryptedFileBuffer), iv.byteLength);
    
    return new Blob([combinedBuffer], { type: 'application/octet-stream' });
  }

  /**
   * Decrypts a small file or file chunk using AES-GCM
   * Used for individual chunks or small files that fit in memory
   */
  async decryptFileChunk(encryptedFile, fileKey) {
    // Extract IV from the beginning of the encrypted file/chunk
    const encryptedFileBuffer = await encryptedFile.arrayBuffer();
    const encryptedView = new Uint8Array(encryptedFileBuffer);
    
    const extractedIV = encryptedView.slice(0, this.IV_SIZE);  // Extract IV (first 12 bytes)
    const encryptedData = encryptedView.slice(this.IV_SIZE);  // Extract encrypted data + auth tag (remaining bytes)
    
    // Note: encryptedData contains both the encrypted content and the 16-byte authentication tag
    // The Web Crypto API automatically handles the auth tag verification during decryption
    const decryptedFileBuffer = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: extractedIV }, fileKey, encryptedData);
    return new Blob([decryptedFileBuffer]);
  }

  /**
   * Imports a public key from a base64 string
   * Used when receiving a public key from another user
   */
  async importPublicKey(base64PublicKey) {
    const publicKeyBuffer = this.base64ToArrayBuffer(base64PublicKey);
    return window.crypto.subtle.importKey(
      "spki",
      publicKeyBuffer,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      false,
      ["encrypt"]
    );
  }

  /**
   * Encrypts file key with public key (logged in user or recipient's)
   * Used for secure file sharing
   */
  async encryptFileKey(fileKey, publicKey) {
    // If publicKey is a string (base64), import it first
    const cryptoKey = typeof publicKey === 'string' 
      ? await this.importPublicKey(publicKey)
      : publicKey;
      
    const fileKeyBuffer = await window.crypto.subtle.exportKey("raw", fileKey);
    const encryptedFileKeyBuffer = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      cryptoKey,
      fileKeyBuffer
    );
    return this.arrayBufferToBase64(encryptedFileKeyBuffer);
  }

  /**
   * Decrypts file key with user's private key
   * Used for secure file sharing
   */
  async decryptFileKey(encryptedFileKey, privateKey) {
    const encryptedFileKeyBuffer = this.base64ToArrayBuffer(encryptedFileKey);
    const decryptedFileKeyBuffer = await window.crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      encryptedFileKeyBuffer
    );
    return window.crypto.subtle.importKey(
      "raw",
      decryptedFileKeyBuffer,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  /**
   * Stores keys in memory
   * Used after successful login or key generation
   */
  setKeys(privateKey, publicKey, keyId) {
    this.privateKey = privateKey;
    this.publicKey = publicKey;
    this.keyId = keyId;
  }

  /**
   * Clears keys from memory
   * Used during logout or session end
   */
  clearKeys() {
    this.privateKey = null;
    this.publicKey = null;
    this.keyId = null;
  }

  /**
   * Checks if keys are ready and available for use
   * Used to verify encryption is fully initialized
   */
  areKeysReady() {
    return this.privateKey !== null && this.publicKey !== null;
  }
  
}

// Export an instance of the service
export const cryptoService = new CryptoService(); 